import { useEffect, useState } from 'react';
import {
    Container,
    Paper,
    Text,
    Title,
    Group,
    Avatar,
    Badge,
    Card,
    Modal,
    Grid,
    Stack,
    Box,
    ActionIcon,
    Center,
    Skeleton,
    rem,
} from '@mantine/core';
import { useDisclosure, useViewportSize } from '@mantine/hooks';
import FAIcon from '../FAIcon';
import Button from '../Button';

interface GoogleReview {
    id: string;
    author_name: string;
    rating: number;
    text: string;
    profile_photo_url: string;
    time: string;
}

interface ReviewStats {
    total_reviews: number;
    average_rating: number;
    place_url: string;
    write_review_url: string;
}
// Sample extensive test data for /api/google-reviews response
const sampleGoogleReviewsResponse = {
    reviews: [
        {
            id: "1",
            author_name: "Mohammed Aleem Akhtar",
            rating: 5,
            text: `I recently had the pleasure of working with Drew Spencer for a thorough review of the condo documents for a property I was considering. From start to finish, his professionalism and expertise were outstanding.

Drew provided an exceptionally detailed analysis of the condo association documents, including the bylaws, declaration, and financial statements. He identified key issues that I would have easily overlooked, such as potential special assessments and restrictions that could impact my decision.

One of the standout qualities was his ability to explain complex legal and financial aspects in a way that was easy to understand. He took the time to walk me through each document and answered all of my questions patiently and thoroughly.

The review was completed in a timely manner, and I received a comprehensive report with clear highlights and recommendations. This efficiency was crucial in helping me make an informed decision without feeling rushed.

Drew displayed a high level of professionalism throughout the process. He was responsive, reliable, and clearly had a deep understanding of condo regulations and real estate practices.

In summary, I highly recommend DREW SPENCER for anyone needing a meticulous and knowledgeable review of condo documents. His expertise and dedication made a significant difference in my property decision, and I feel much more confident moving forward.

Thank you, Drew for your exceptional service!`,
            profile_photo_url: "/reviews/profile1.png",
            time: "4 months ago"
        },
        {
            id: "2",
            author_name: "Khailla and Anthony Loria",
            rating: 5,
            text: `As first time home buyers, Drew was very helpful and informative to me and my partner.Every detail that we needed to know about the condo property we were looking at was thoroughly explained during the meeting! Both me and my partner are confident with Drew's skills and years of experience in this field, and we can confidently say that the service was top tier! All questions that we asked were answered, and even after the Zoom meeting, Drew continues to provide assistance for any questions we have. This is something every homeowners, especially first time homeowners cannot take for granted and simply cannot find anywhere else! 5 stars!!`,
            profile_photo_url: "/reviews/profile2.png",
            time: "5 months ago"
        },
        {
            id: "3",
            author_name: "Al Denino",
            rating: 5,
            text:  `I have worked with Drew on two condo purchases this year and he has done an outstanding job in helping me navigate the documents and ensuring I assess the risks and negotiate with the seller accordingly. Would recommend his services to anyone looking to purchase a condo.`, 
            profile_photo_url: "/reviews/profile3.png",
            time: "5 months ago"
        },
        {
            id: "4",
            author_name: "Marc Francois",
            rating: 5,
            text: "Drew was quick, thorough, and very knowledgeable with the condo document review. He really gave us peace of mind during our house hunt. Highly recommend!",
            profile_photo_url: "/reviews/profile4.png",
            time: "6 months ago"
        },
        {
            id: "5",
            author_name: "Murray Valiaho",
            rating: 5,
            text:   `Absolutely thrilled with the service provided by Drew Spencer of Home Health Solutions! Drew recently conducted a thorough review of our condo documents via Zoom, and I couldn't be happier with the experience. From start to finish, Drew was an absolute pleasure to work with.
Drew's communication skills are top-notch. He made the process incredibly easy and comfortable, ensuring that all my questions were addressed and that I fully understood every aspect of the document review. His friendly and approachable demeanor immediately put me at ease, making it feel more like a conversation with a knowledgeable friend rather than a formal consultation.
He was prompt in answering emails and scheduling the Zoom call, I never felt like I was waiting around for information, which was incredibly refreshing.
Most importantly, Drew provided invaluable information during the document review. His expertise and attention to detail were evident throughout the process, and I felt confident that I was receiving accurate and reliable insights. He highlighted key points, clarified any areas of confusion, and ultimately empowered me with the knowledge needed to make informed decisions regarding my condo.
Overall, I can't recommend Drew Spencer and Home Health Solutions highly enough. If you're in need of a professional who is easy to talk to, quick to respond, and provides invaluable information. Thanks again, Drew, for your outstanding service!`,
            profile_photo_url: "/reviews/profile5.png",
            time: "8 month ago"
        },
        {
            id: "6",
            author_name: "Melanie Davidson",
            rating: 5,
            text:  `Drew is fantastic! Needed a condo review done on short notice and quick turnaround for my first home purchase and he came through on this, very thorough and organized and managed to explain things in a way that made a lot of new info very easy to understand for a first time buyer. Highly recommend!`,
            profile_photo_url: "/reviews/profile6.png",
            time: "7 month ago"
        }
    ],
    average_rating: 5,
    total_reviews: 44,
    place_url: "https://search.google.com/local/reviews?placeid=YOUR_PLACE_ID",
    write_review_url: "https://search.google.com/local/writereview?placeid=YOUR_PLACE_ID"
};
export function TestimonialsSection() {
    const [reviews, setReviews] = useState<GoogleReview[]>([]);
    const [stats, setStats] = useState<ReviewStats>({
        total_reviews: 44,
        average_rating: 5,
        place_url: '',
        write_review_url: '',
    });
    const [selectedReview, setSelectedReview] = useState<GoogleReview | null>(null);
    const [opened, { open, close }] = useDisclosure(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { width } = useViewportSize();

    useEffect(() => {
        const fetchReviews = async () => {
            try {
                setLoading(true);
                // Using sample data instead of API
                setReviews(sampleGoogleReviewsResponse.reviews);
                setStats({
                    total_reviews: sampleGoogleReviewsResponse.total_reviews,
                    average_rating: sampleGoogleReviewsResponse.average_rating,
                    place_url: sampleGoogleReviewsResponse.place_url,
                    write_review_url: sampleGoogleReviewsResponse.write_review_url,
                });
            } catch (err) {
                setError('Unable to load reviews at this time');
                console.error('Error loading reviews:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchReviews();
    }, []);

    const handleReviewClick = (review: GoogleReview) => {
        setSelectedReview(review);
        open();
    };

    const RatingStars = ({ rating }: { rating: number }) => (
        <Group gap={2}>
            {[...Array(5)].map((_, i) => (
                <FAIcon
                    key={i}
                    name="star"
                    size={14}
                    style={{ color: i < rating ? '#FFD700' : '#E0E0E0' }}
                />
            ))}
        </Group>
    );

    const ReviewCard = ({ review }: { review: GoogleReview }) => (
        <Card
            shadow="sm"
            padding="lg"
            radius="md"
            withBorder
            style={{ height: '100%', cursor: 'pointer' }}
            className="transition-all duration-200 hover:shadow-lg"
            onClick={() => handleReviewClick(review)}
        >
            <Stack h="100%">
                <Group>
                    <Avatar src={review.profile_photo_url} size="md" radius="xl" />
                    <Box>
                        <Text fw={500}>{review.author_name}</Text>
                        <RatingStars rating={review.rating} />
                    </Box>
                </Group>

                <Box pos="relative" style={{ flex: 1 }}>
                    <FAIcon
                        name="quote-right"
                        size={24}
                        style={{
                            opacity: 0.15,
                            position: 'absolute',
                            top: -10,
                            left: -5
                        }}
                    />
                    <Text
                        lineClamp={4}
                        size="sm"
                        mt={5}
                        style={{
                            position: 'relative',
                            paddingLeft: rem(20)
                        }}
                    >
                        {review.text}
                    </Text>
                </Box>

                <Group gap="xs" mt="auto">
                    <FAIcon name="google" prefix="fab" size={16} style={{ opacity: 0.7 }} />
                    <Text size="xs" c="dimmed">
                        {review.time}
                    </Text>
                </Group>
            </Stack>
        </Card>
    );

    const ReviewSkeleton = () => (
        <Paper p="lg" radius="md" withBorder>
            <Group mb="md">
                <Skeleton height={40} circle />
                <Box style={{ flex: 1 }}>
                    <Skeleton height={10} width="60%" mb={8} />
                    <Skeleton height={10} width="40%" />
                </Box>
            </Group>
            <Skeleton height={60} />
            <Skeleton height={10} mt="md" width="30%" />
        </Paper>
    );

    return (
        <Container size="xl" py={50}>
            <Paper radius="lg" withBorder p="xl" mb={30}>
                <Group justify="space-between" wrap="wrap" gap="xl">
                    <Box>
                        <Title order={2} size="h1" mb="xs">Customer Reviews</Title>
                        <Group align="center">
                            <FAIcon name="google" prefix="fab" size={24} />
                            <Text size="xl" fw={700}>{stats.average_rating.toFixed(1)}</Text>
                            <RatingStars rating={Math.round(stats.average_rating)} />
                            <Badge size="lg" variant="light">
                                {stats.total_reviews} reviews
                            </Badge>
                        </Group>
                    </Box>
                    <Group>
                        {/* <Box
                            component="a"
                            href="https://g.page/r/CTNI8Nycyh7pEBE/review"
                            target="_blank"
                            style={{ textDecoration: 'none' }}
                        > */}
                            <Button
                            translate='no'
                            transition
                            size='small'
                            component='a'
                            href="https://g.page/r/CTNI8Nycyh7pEBE/review"
                            // target="_blank"
                            // variant='outline'
                            // color='dark'
                            variant='filled'
                            // color='primary'
                            >
                                <Group gap="md" align="center">
                                    <FAIcon name="star" size={20} style={{ color: '#FFD700' }} />
                                    <Stack gap={2}>
                                        <Text fw={600} size="md" c="white">Write a Review</Text>
                                        <Text size="xs" c="white" opacity={0.9}>Share your experience</Text>
                                    </Stack>
                                </Group>
                            </Button>
                        {/* </Box> */}
                    </Group>
                </Group>
            </Paper>

            {error ? (
                <Text c="red" ta="center">{error}</Text>
            ) : (
                <Grid gutter="lg">
                    {loading
                        ? Array.from({ length: 6 }).map((_, i) => (
                            <Grid.Col key={i} span={{ base: 12, sm: 6, md: 4 }}>
                                <ReviewSkeleton />
                            </Grid.Col>
                        ))
                        : reviews.length === 0 ? 
                            <Grid.Col key="noreview" span={{ base: 12, sm: 6, md: 4 }}>
                            </Grid.Col> 
                        : reviews.map((review) => (
                            <Grid.Col key={review.id} span={{ base: 12, sm: 6, md: 4 }}>
                                <ReviewCard review={review} />
                            </Grid.Col>
                        ))}
                </Grid>
            )}

            <Modal
                opened={opened}
                onClose={close}
                size={width < 768 ? '100%' : 'lg'}
                padding="xl"
                radius="md"
                title={
                    selectedReview && (
                        <Group>
                            <Avatar src={selectedReview.profile_photo_url} size="lg" radius="xl" />
                            <Box>
                                <Text fw={700} size="lg">{selectedReview.author_name}</Text>
                                <RatingStars rating={selectedReview.rating} />
                            </Box>
                        </Group>
                    )
                }
            >
                {selectedReview && (
                    <Stack>
                        <Text size="md" style={{ whiteSpace: 'pre-wrap' }}>
                            {selectedReview.text}
                        </Text>
                        <Group gap="xs">
                            <FAIcon name="google" prefix="fab" size={16} style={{ opacity: 0.7 }} />
                            <Text size="sm" c="dimmed">
                                Posted {selectedReview.time}
                            </Text>
                        </Group>
                    </Stack>
                )}
            </Modal>
        </Container>
    );
}

export default TestimonialsSection;
